/**
 * html.tomake.design — main UI.
 *
 * Ported from the legacy create-figma-plugin `ui.tsx`: the tabbed converter
 * (URL / Code / File / Settings) with lazy-loaded screens, the sandbox
 * handshake (user info, saved data, fonts), API health + user-status polling,
 * and the upgrade modal. The UI uses the real @create-figma-plugin/ui component
 * library (the app runs on preact/compat — see vite.config.ts + main.tsx); this
 * wraps everything in the context StoreProvider.
 */


import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import {
  LoadingIndicator,
  IconLink24,
  IconImport24,
  IconCodeBlock24,
  IconSettings24,
  IconLinkConnected24,
} from './ui-kit';
import { emit, on } from './messaging';
import { StoreProvider, useStore, actions } from './store';
import { extractH2figBytes } from './utils/clipboard-import';
import { importCapturePayload } from './utils/import-capture';
import { decryptH2Fig } from './utils/decryption';
import { api } from './api';
import { useHealthQuery, useUserStatusQuery } from './queries';
import { usageTrackingService } from './services/UsageTrackingService';
import { userService } from './services/UserService';
import { setErrorReportingUserId, flushErrors, getLogger, forwardErrorBatch, type ErrorBatch } from './helpers/logger';
import Header from './components/common/Header';
import PluginConnecting from './components/common/PluginConnecting';
import { appConfig } from './config/app-config';
import { withChunkRecovery } from './utils/lazy-with-recovery';

const uiLogger = getLogger('UI');

// Tab icons from @create-figma-plugin/ui (they inherit the tab text color).
function TabIcon({ tab }: { tab: string }) {
  switch (tab) {
    case 'Web':
      return <IconLink24 />;
    case 'File':
      return <IconImport24 />;
    case 'Editor':
      return <IconCodeBlock24 />;
    case 'Extension':
      return <IconLinkConnected24 />;
    default:
      return null;
  }
}

/**
 * Tabs that carry a "Beta" pill.
 *
 * Empty since the Storybook tab was withdrawn — it was the only member, and for the reason the
 * pill existed to admit: discovery reads whichever catalogue a library happens to publish, the
 * story index format differs between Storybook and Histoire (and between Storybook majors), and
 * a story that renders nothing at the runner's root selector comes back as an empty frame rather
 * than an error. The remaining tabs capture a URL, a file, or pasted markup — all of which
 * either work or fail loudly, which is why none of them is labelled beta.
 *
 * Kept rather than deleted because the pill is the mechanism, not the tab: the next unsettled
 * capture path goes in here. `every beta name matches a tab that exists` guards the pairing.
 *
 * A set rather than an `if` in the JSX so the tab bar keeps rendering from one list.
 */
const BETA_TABS = new Set<string>([]);

/**
 * What each tab is CALLED, separate from the id it is keyed by.
 *
 * The ids are the original internal names and the analytics mapping above has always translated
 * them — `Web` is reported as `url`, `Editor` as `code`. The tab bar was the one place still
 * showing the internal name to a person, so the UI said "Web" and "Editor" while every other
 * surface called them URL and Code. This renames the labels and leaves the ids alone, so the
 * state values, the analytics keys and every `activeTab === 'Web'` branch are untouched.
 *
 * A lookup rather than an array of objects because the bar still renders from one flat list of
 * names, which is what `beta-label.test.ts` reads to pair BETA_TABS against the real tabs.
 */
const TAB_LABELS: Record<string, string> = { Web: 'URL', Editor: 'Code', File: 'File' };

/** How long to wait for the sandbox's `USER_INFO`. See `timeouts.handshakeMs` in config/app-config.ts. */
const HANDSHAKE_TIMEOUT_MS = appConfig.timeouts.handshakeMs;

/**
 * Wrapped because a deploy replaces every chunk's content-hashed filename, so a session still
 * running the previous build asks for a file that no longer exists the next time a tab is
 * opened. Captured in production: ZipImport and StorybookImport both failed this way within an
 * hour of error logging existing. See utils/lazy-with-recovery.ts.
 */
const WebsiteCapture = lazy(withChunkRecovery('WebsiteCapture', () => import('./components/WebsiteCapture')));
const ExtensionInbox = lazy(withChunkRecovery('ExtensionInbox', () => import('./components/ExtensionInbox')));
const HtmlCssCapture = lazy(withChunkRecovery('HtmlCssCapture', () => import('./components/HtmlCssCapture')));
const ZipImport = lazy(withChunkRecovery('ZipImport', () => import('./components/ZipImport')));
const AccountSettings = lazy(withChunkRecovery('AccountSettings', () => import('./components/AccountSettings')));
const UpgradeModal = lazy(withChunkRecovery('UpgradeModal', () => import('./components/UpgradeModal')));
const ImportCompleted = lazy(withChunkRecovery('ImportCompleted', () => import('./components/ImportCompleted')));

function Plugin() {
  const { state, dispatch } = useStore();
  const [activeTab, setActiveTab] = useState('Web');
  const [apiStatus, setApiStatus] = useState('checking');
  const [hasInitialized, setHasInitialized] = useState(false);
  const mountedRef = useRef(true);
  const healthQuery = useHealthQuery();
  const userStatusQuery = useUserStatusQuery(state.figmaUID);

  // Track tab switches.
  useEffect(() => {
    const tabMapping: { [key: string]: 'url' | 'code' | 'file' | 'account' } = {
      Web: 'url',
      Editor: 'code',
      File: 'file',
      Extension: 'file',
      Settings: 'account',
    };
    const trackingKey = tabMapping[activeTab];
    if (trackingKey) usageTrackingService.trackTabSwitch(trackingKey);
  }, [activeTab]);

  // Reset the "import succeeded" flag after it flips (was dispatched in-render
  // in the legacy UI, which React forbids).
  useEffect(() => {
    if (state.isSuccess) dispatch(actions.resetImport());
  }, [state.isSuccess, dispatch]);


  // Derive apiStatus from the health query (replaces the old imperative
  // checkApiHealth debounce).
  useEffect(() => {
    if (healthQuery.isError) {
      setApiStatus('offline');
      if (hasInitialized) {
        dispatch(
          actions.setError(
            'Unable to connect to the design conversion service. Check your internet connection.',
          ),
        );
      } else {
        setHasInitialized(true);
      }
    } else if (healthQuery.data?.status === 'healthy') {
      setApiStatus('online');
      if (!hasInitialized) setHasInitialized(true);
    } else if (healthQuery.isSuccess) {
      setApiStatus('offline');
      if (hasInitialized) {
        dispatch(
          actions.setError(
            'The design conversion service appears to be experiencing issues. Some features may not work correctly.',
          ),
        );
      } else {
        setHasInitialized(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [healthQuery.data, healthQuery.isError, healthQuery.isSuccess]);

  // Global ⌘/Ctrl+V import: the browser extension's "Copy for plugin" puts an
  // H2FIG marker envelope on the clipboard. Paste ANYWHERE in the plugin and
  // the capture imports — no paste box, no tab hunting. Ordinary pastes (into
  // text fields, etc.) carry no marker and pass through untouched.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const html = e.clipboardData?.getData('text/html') || '';
      const text = e.clipboardData?.getData('text/plain') || '';
      const bytes = extractH2figBytes({ html, text });
      if (!bytes) return; // not a capture — let the paste happen normally
      e.preventDefault();
      (async () => {
        try {
          const payload = await decryptH2Fig(
            bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer,
          );
          setActiveTab('Extension');
          dispatch(actions.setError(null));
          dispatch(actions.setLoading(true));
          importCapturePayload(payload, state.options);
        } catch (err) {
          uiLogger.error('Clipboard import failed:', err);
          dispatch(actions.setError('Could not import the pasted capture — copy it again from the extension.'));
        }
      })();
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [state.options, dispatch]);

  // Initial data loading + plugin message listeners.
  useEffect(() => {
    mountedRef.current = true;
    usageTrackingService.trackSession();
    emit('GET_USER_INFO');
    emit('LOAD_DATA');
    emit('GET_AVAILABLE_FONTS');

    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has('file')) setActiveTab('File');

    const eventCleanups: Array<() => void> = [];

    eventCleanups.push(
      on('USER_INFO', (data: any) => {
        if (!mountedRef.current) return;
        if (data && data.id) {
          dispatch(actions.setUserInfo(data.id, data.name || ''));
          setErrorReportingUserId(data.id);
        } else {
          uiLogger.warn('Received empty user info');
        }
      }),
    );

    eventCleanups.push(
      on('PLUGIN_DATA', (data: any) => {
        if (!mountedRef.current) return;
        if (data && data.data) {
          if (data.data.options) {
            Object.entries(data.data.options).forEach(([key, value]) => {
              dispatch(actions.updateOption(key, value));
            });
          }
          if (data.data.savedWebsites && Array.isArray(data.data.savedWebsites)) {
            dispatch(actions.setSavedWebsites(data.data.savedWebsites));
          }
          if (data.data.usageStats) usageTrackingService.setStats(data.data.usageStats);
          if (data.data.userStatus) {
            const cached = data.data.userStatus;
            const cacheAge = Date.now() - (cached.lastChecked || 0);
            if (cacheAge < 5 * 60 * 1000) {
              if (cached.membership) dispatch(actions.setMembership(cached.membership));
              if (cached.isBlocked) {
                dispatch(actions.setBlocked(cached.isBlocked, cached.blockedUntil));
                if (cached.blockedUntil && Date.now() < cached.blockedUntil) {
                  dispatch(
                    actions.openUpgradeModal(
                      'quota_exceeded',
                      new Date(cached.blockedUntil).toISOString(),
                    ),
                  );
                }
              }
            }
          }
          if (!data.data.hasSeenUpgradeModal) {
            setTimeout(() => {
              if (mountedRef.current) {
                dispatch(actions.openUpgradeModal('welcome', null));
                emit('SAVE_DATA', 'hasSeenUpgradeModal', true);
              }
            }, 500);
          }
        }
      }),
    );

    // Error reports from the plugin sandbox. There is no fetch in there, so the message channel
    // is the only way out; the UI's transport is what actually delivers them.
    eventCleanups.push(
      on('LOG_ERRORS', (batch: ErrorBatch) => {
        forwardErrorBatch(batch);
      }),
    );

    eventCleanups.push(
      on('CONVERSION_COMPLETE', (data: any) => {
        if (!mountedRef.current) return;
        dispatch(actions.setLoading(false));
        dispatch(actions.setSuccess(true));
        window.dispatchEvent(new CustomEvent('CONVERSION_COMPLETE'));

        // Show the "Import completed" modal only when it has something to
        // offer: missing fonts the designer can re-map on the canvas.
        const fontReplacements = Array.isArray(data?.fontReplacements)
          ? data.fontReplacements
          : [];
        if (fontReplacements.length > 0) {
          dispatch(
            actions.openImportCompleted({
              sectionId: data?.sectionId ?? null,
              frameIds: Array.isArray(data?.frameIds) ? data.frameIds : [],
              fontReplacements,
            }),
          );
        }
      }),
    );

    eventCleanups.push(
      on('CONVERSION_ERROR', (data: any) => {
        if (!mountedRef.current) return;
        dispatch(actions.setLoading(false));
        dispatch(actions.setError(data?.error || 'An unknown error occurred'));
        window.dispatchEvent(
          new CustomEvent('CONVERSION_ERROR', { detail: { message: data?.error } }),
        );
      }),
    );

    eventCleanups.push(
      on('CONVERSION_PROGRESS', (data: any) => {
        if (!mountedRef.current) return;
        uiLogger.info('Conversion progress:', data);
      }),
    );

    eventCleanups.push(
      on('AVAILABLE_FONTS', (data: any) => {
        if (!mountedRef.current) return;
        if (Array.isArray(data?.fonts) && data.fonts.length > 0) {
          const fontOptions = data.fonts.map((font: string) => ({ value: font, text: font }));
          fontOptions.sort((a: any, b: any) => a.text.localeCompare(b.text));
          if (!fontOptions.some((font: any) => font.value === 'Inter')) {
            fontOptions.unshift({ value: 'Inter', text: 'Inter' });
          }
          dispatch(actions.setAvailableFonts(fontOptions));
          if (!data.fonts.includes(state.options.defaultFontFamily)) {
            dispatch(actions.updateOption('defaultFontFamily', 'Inter'));
          }
        } else {
          dispatch(
            actions.setAvailableFonts([
              { value: 'Inter', text: 'Inter' },
              { value: 'Arial', text: 'Arial' },
              { value: 'Helvetica', text: 'Helvetica' },
              { value: 'SF Pro', text: 'SF Pro' },
              { value: 'Roboto', text: 'Roboto' },
            ]),
          );
        }
      }),
    );

    const unsubscribeFromConnectionStatus = api.onConnectionStatusChange((status: any) => {
      if (!mountedRef.current) return;
      setApiStatus(status);
      if (status === 'offline' && hasInitialized) {
        dispatch(
          actions.setError('Connection to design service lost. Check your internet connection.'),
        );
      } else if (status === 'online' && hasInitialized && state.error) {
        if (
          state.error.includes('connection') ||
          state.error.includes('Internet') ||
          state.error.includes('API') ||
          state.error.includes('service')
        ) {
          dispatch(actions.setError(null));
        }
      }
    });

    return () => {
      mountedRef.current = false;
      unsubscribeFromConnectionStatus();
      eventCleanups.forEach((cleanup) => cleanup());
      flushErrors();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // React to the user-status query result once we have the figma UID.
  useEffect(() => {
    if (apiStatus === 'offline') return;
    if (state.isBlocked && state.blockedUntil && Date.now() < state.blockedUntil) return;
    const userStatus = userStatusQuery.data;
    if (!userStatus || !mountedRef.current) return;
    if (userStatus.blocked) {
      const blockedUntil = Date.now() + 60 * 60 * 1000;
      const blockMessage =
        userStatus.message ||
        'Access temporarily blocked due to suspicious activity. Please try again later.';
      dispatch(actions.setBlocked(true, blockedUntil));
      dispatch(actions.openUpgradeModal('quota_exceeded', new Date(blockedUntil).toISOString()));
      emit('SAVE_DATA', 'userStatus', {
        membership: state.membership,
        isBlocked: true,
        blockedUntil,
        blockMessage,
        lastChecked: Date.now(),
      });
      return;
    }
    if (userStatus.isSuccess) {
      if (state.isBlocked) dispatch(actions.setBlocked(false));
      const membershipType = userStatus.membership || 'FREE';
      dispatch(actions.setMembership(membershipType));
      emit('SAVE_DATA', 'userStatus', {
        membership: membershipType,
        rateLimit: userStatus.rateLimit,
        isBlocked: false,
        blockedUntil: null,
        lastChecked: Date.now(),
      });
      if (userStatus.rateLimit) {
        const nextAvailable = new Date(userStatus.rateLimit.nextAvailableTime);
        if (nextAvailable > new Date() && membershipType !== 'PRO') {
          dispatch(
            actions.openUpgradeModal('quota_exceeded', userStatus.rateLimit.nextAvailableTime),
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userStatusQuery.data]);

  // Persist options when they change.
  useEffect(() => {
    if (!state.options || !mountedRef.current) return;
    if (Object.keys(state.options).length > 0 && state.options.defaultFontFamily) {
      emit('SAVE_DATA', 'options', state.options);
    }
  }, [state.options]);

  const handleUpgradeModalClose = useCallback(async () => {
    dispatch(actions.closeUpgradeModal());
    if (state.figmaUID) {
      try {
        userService.clearCache();
        userService.setUserInfo(state.figmaUID, state.figmaUserName);
        const userStatus = await api.getUserStatus(state.figmaUID);
        if (userStatus.isSuccess) {
          const membershipType = userStatus.membership || 'FREE';
          if (membershipType !== state.membership) {
            dispatch(actions.setMembership(membershipType));
            emit('SAVE_DATA', 'userStatus', {
              membership: membershipType,
              rateLimit: userStatus.rateLimit,
              isBlocked: false,
              blockedUntil: null,
              lastChecked: Date.now(),
            });
          }
        }
      } catch (error) {
        uiLogger.warn('Failed to refresh account status after modal close:', error);
      }
    }
  }, [state.figmaUID, state.figmaUserName, state.membership, dispatch]);

  /**
   * The sandbox handshake, as a render state.
   *
   * `figmaUID` is set by exactly one thing — the USER_INFO reply — so it doubles as "the sandbox
   * answered". The timer is what keeps this from being an unbounded spinner: `handshakeTimedOut`
   * flips the screen from "connecting" to a message with a Try again on it.
   *
   * Keyed on `handshakeAttempt` as well as `isConnected` so a retry restarts the clock; without
   * that the effect would not re-run and a second attempt would render as already-failed.
   */
  const isConnected = Boolean(state.figmaUID);
  const [handshakeTimedOut, setHandshakeTimedOut] = useState(false);
  const [handshakeAttempt, setHandshakeAttempt] = useState(0);

  useEffect(() => {
    if (isConnected) return;
    // Only the timer sets the flag. The reset lives in `retryHandshake`, where the retry actually
    // happens — resetting it here too would be a synchronous setState in an effect body, which
    // cascades a render on every mount for no benefit.
    const timer = setTimeout(() => {
      if (mountedRef.current) setHandshakeTimedOut(true);
    }, HANDSHAKE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isConnected, handshakeAttempt]);

  // All three, not just USER_INFO: if the sandbox missed the first round it missed the saved
  // options and the font list too, and reconnecting without them leaves the plugin connected and
  // half-configured — no remembered options, and an empty font dropdown in every modal.
  const retryHandshake = useCallback(() => {
    // Back to the spinner, and bump the attempt so the timer effect re-runs and restarts the clock.
    setHandshakeTimedOut(false);
    setHandshakeAttempt((attempt) => attempt + 1);
    emit('GET_USER_INFO');
    emit('LOAD_DATA');
    emit('GET_AVAILABLE_FONTS');
  }, []);

  // Nothing in here works without the sandbox: `figmaUID` is the first thing every capture path
  // checks, and it only exists once USER_INFO has come back. Rendering the tabs before then put an
  // enabled Import button in front of the user whose only possible outcome was "Figma user ID is
  // required" — an error about an internal identifier, for a state they did not cause.
  //
  // Below every hook, deliberately: an early return above one would change the hook order between
  // the connecting and connected renders.
  if (!isConnected) {
    return <PluginConnecting timedOut={handshakeTimedOut} onRetry={retryHandshake} />;
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Header />

        <div className="custom-icon-tabs">
          <div className="tab-buttons">
            {(['Web', 'Editor', 'File'] as const).map((tab) => (
              <button
                key={tab}
                className={`tab-button ${activeTab === tab ? 'active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                <TabIcon tab={tab} />
                <span>{TAB_LABELS[tab]}</span>
                {BETA_TABS.has(tab) && <span className="tab-beta">Beta</span>}
              </button>
            ))}
            <button
              className={`tab-button settings-tab ${activeTab === 'Settings' ? 'active' : ''}`}
              onClick={() => setActiveTab('Settings')}
              title="Settings"
              aria-label="Settings"
            >
              <IconSettings24 />
              <span>Settings</span>
            </button>
          </div>

          <div className="tab-content">
            {activeTab === 'Web' && (
              <Suspense fallback={<LoadingIndicator />}>
                <WebsiteCapture />
              </Suspense>
            )}
            {activeTab === 'File' && (
              <Suspense fallback={<LoadingIndicator />}>
                <ZipImport />
              </Suspense>
            )}
            {/* Hidden: 'Extension' is off the tab bar above. The branch stays so putting it
                back is a one-word change rather than a re-import. */}
            {activeTab === 'Extension' && (
              <Suspense fallback={<LoadingIndicator />}>
                <ExtensionInbox />
              </Suspense>
            )}
            {activeTab === 'Editor' && (
              <Suspense fallback={<LoadingIndicator />}>
                <HtmlCssCapture />
              </Suspense>
            )}
            {activeTab === 'Settings' && (
              <Suspense fallback={<LoadingIndicator />}>
                <AccountSettings />
              </Suspense>
            )}
          </div>
        </div>

        <style>{`
          .custom-icon-tabs { display: flex; flex-direction: column; height: 100%; }
          .tab-buttons { display: flex; border-bottom: 1px solid var(--figma-color-border); background: var(--figma-color-bg); position: relative; flex-shrink: 0; }
          .tab-button { display: flex; align-items: center; justify-content: center; gap: 6px; padding: 10px 16px; border: none; background: transparent; color: var(--figma-color-text-secondary); font-size: 11px; font-weight: 500; cursor: pointer; transition: all 0.2s ease; position: relative; border-bottom: 2px solid transparent; flex: 1; min-width: 0; }
          .tab-button:hover { color: var(--figma-color-text); background: var(--figma-color-bg-hover); }
          .tab-button.active { color: var(--figma-color-text); border-bottom-color: var(--figma-color-bg-brand); }
          .tab-button.settings-tab { margin-left: auto; border-left: 1px solid var(--figma-color-border); flex: 0 0 auto; min-width: auto; padding: 10px 14px; }
          .tab-button svg { width: 16px; height: 16px; flex-shrink: 0; }
          /* The pill sits inline after the label. No tab carries one since Storybook was
             withdrawn, so this styles nothing today — kept because BETA_TABS is still the
             mechanism and the next unsettled capture path will use it. The measurement that
             justified it: at 480px each tab box was ~109px and the Storybook row needed ~109px
             with the pill, spilling into the button's own padding, which is empty space;
             clearance to the neighbouring tabs' contents was 29px and 35px. That was taken with
             four tabs and there are three now, so a future beta tab has more room, not less.
             flex-shrink:0 keeps it from being squeezed to an unreadable sliver if a wider UI
             font is ever loaded.
             (No backticks in here: this block is a JS template literal, and one would end it.) */
          .tab-beta { font-size: 8px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; padding: 1px 4px; border-radius: 3px; background: var(--figma-color-bg-brand-tertiary); color: var(--figma-color-text-brand); flex-shrink: 0; }
          .tab-content { padding: 0; flex: 1; overflow-y: auto; height: 100%; }
        `}</style>
      </div>

      <Suspense fallback={null}>
        <UpgradeModal
          open={state.upgradeModal.isOpen}
          onClose={handleUpgradeModalClose}
          reason={state.upgradeModal.reason}
          rateLimitEndTime={state.upgradeModal.rateLimitEndTime}
        />
      </Suspense>

      <Suspense fallback={null}>
        <ImportCompleted />
      </Suspense>
    </div>
  );
}

function App() {
  return (
    <StoreProvider>
      <Plugin />
    </StoreProvider>
  );
}

export default App;
